<template>
    <card plain class="m-0">
        <div class="template-info">
            <div>
                <strong>Patient Rights PDF</strong>
                <div class="text-muted small">
                    <span v-if="!value" class="badge badge-warning">Not Configured</span>
                    <span v-else class="badge badge-primary">PDF Uploaded</span>
                </div>
            </div>
            <div class="template-actions mt-2">
                <b-button
                    v-if="value"
                    variant="outline-danger"
                    size="sm"
                    @click="removeDocument"
                    class="mr-2 mb-2">
                    <i class="fas fa-trash"></i>
                    Remove
                </b-button>
                <b-button
                    variant="primary"
                    size="sm"
                    :disabled="uploading"
                    @click="$refs.fileInput.click()"
                    class="mr-2 mb-2">
                    <i v-if="uploading" class="fas fa-spinner fa-spin"></i>
                    <i v-else class="fas fa-upload"></i>
                    {{ value ? 'Replace' : 'Upload' }} PDF
                </b-button>
            </div>
        </div>

        <input
            ref="fileInput"
            type="file"
            accept=".pdf,application/pdf"
            style="display: none"
            @change="handleFileSelection" />

        <div v-if="uploadProgress > 0 && uploadProgress < 100" class="mb-3">
            <b-progress :value="uploadProgress" :max="100" animated>
                <span>{{ Math.round(uploadProgress) }}%</span>
            </b-progress>
        </div>

        <div v-if="errorMessage" class="alert alert-danger">
            {{ errorMessage }}
        </div>

        <b-modal
            id="remove-patient-rights-modal"
            ref="removeModal"
            title="Remove Patient Rights PDF"
            ok-title="Remove"
            ok-variant="danger"
            cancel-title="Cancel"
            @ok="confirmRemove">
            <p>Are you sure you want to remove the configured Patient Rights PDF?</p>
            <p class="text-muted">The system will attempt to use a default PDF on disk if one is configured; otherwise, patients will see a 'Patient rights PDF not configured' error until a new document is uploaded.</p>
        </b-modal>
    </card>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import axios from 'axios';
import Card from '@/components/Cards/Card.vue';

@Component({
    name: 'PatientRightsPdfUpload',
    components: {
        Card,
    },
})
export default class PatientRightsPdfUpload extends Vue {
    @Prop({ required: false, default: null })
    readonly value!: number | null;

    private uploading: boolean = false;
    private uploadProgress: number = 0;
    private errorMessage: string = '';

    $refs!: {
        fileInput: HTMLInputElement;
        removeModal: any;
    };

    private handleFileSelection(event: Event) {
        const target = event.target as HTMLInputElement;
        if (target.files && target.files.length > 0) {
            const file = target.files[0];
            this.uploadDocument(file);
        }
    }

    private async uploadDocument(file: File) {
        if (!file.name.toLowerCase().endsWith('.pdf') || file.type !== 'application/pdf') {
            this.errorMessage = 'Please select a valid PDF file';
            return;
        }

        this.errorMessage = '';
        this.uploadProgress = 0;
        this.uploading = true;

        const formData = new FormData();
        formData.append('files', file);

        try {
            const response = await axios.post('/api/Image/SaveDocument?bypassIngestion=true', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                onUploadProgress: (progressEvent) => {
                    if (progressEvent.total) {
                        this.uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    }
                }
            });

            const imageIds = response.data as number[];
            if (imageIds && imageIds.length > 0) {
                this.$emit('input', imageIds[0]);
                this.uploadProgress = 0;
            } else {
                this.errorMessage = 'Upload failed: No image ID returned';
            }
        } catch (error) {
            if (axios.isAxiosError(error)) {
                if (error.response?.data) {
                    const errorData = error.response.data;
                    if (typeof errorData === 'string') {
                        this.errorMessage = errorData;
                    } else if (errorData.message) {
                        this.errorMessage = errorData.message;
                    } else {
                        this.errorMessage = JSON.stringify(errorData);
                    }
                } else {
                    this.errorMessage = error.message || 'Failed to upload PDF';
                }
            } else {
                this.errorMessage = 'Failed to upload PDF';
            }
            this.uploadProgress = 0;
        } finally {
            this.uploading = false;
        }

        // Clear file input
        const fileInput = this.$refs.fileInput as HTMLInputElement;
        if (fileInput) {
            fileInput.value = '';
        }
    }

    private removeDocument() {
        this.$refs.removeModal.show();
    }

    private confirmRemove() {
        this.$emit('input', null);
    }
}
</script>

<style scoped>
.template-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0;
}
</style>
